import * as THREE from 'three'
import type { PowerUpType } from '@/src/stores/runStateTypes'
import { markForDisposal } from '../../../utils/threeDisposal'
import type { PowerUpTextures } from './powerUpTextures'

const powerUpIconNodeNameByType: Record<PowerUpType, string> = {
    battery: 'Battery_low',
    plane: 'Star_low',
    shield: 'Shield_low',
    heart: '',
    coin: '',
}

const allIconNodeNames = new Set(Object.values(powerUpIconNodeNameByType))

export const powerUpColorByType: Record<PowerUpType, string> = {
    battery: '#bfff28',
    plane: '#a78bfa',
    shield: '#4da3ff',
    heart: '#ff5252',
    coin: '#ffd54a',
}

export const GLOW_BILLBOARD_SETTINGS = {
    size: 2.35,
    opacity: 0.475,
}

const POWERUP_GLOW_SETTINGS = {
    opacity: 1.5,
    power: 2,
    brightness: 1,
}

const POWERUP_ORB_MATCAP_TINT = '#ffffff'

let cachedGlowTexture: THREE.Texture | null = null

let cachedDustSpriteTexture: THREE.Texture | null = null

export function getDustSpriteTexture(): THREE.Texture {
    if (cachedDustSpriteTexture) return cachedDustSpriteTexture

    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = 'rgba(255, 255, 255, 1)'
    ctx.beginPath()
    ctx.arc(size * 0.5, size * 0.5, size * 0.32, 0, Math.PI * 2)
    ctx.closePath()
    ctx.fill()

    cachedDustSpriteTexture = new THREE.CanvasTexture(canvas)
    cachedDustSpriteTexture.colorSpace = THREE.SRGBColorSpace
    return cachedDustSpriteTexture
}

function getGlowTexture(): THREE.Texture {
    if (cachedGlowTexture) return cachedGlowTexture

    const size = 128
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')!

    const gradient = ctx.createRadialGradient(
        size * 0.5, size * 0.5, 0,
        size * 0.5, size * 0.5, size * 0.5,
    )
    gradient.addColorStop(0, 'rgba(255,255,255,1)')
    gradient.addColorStop(0.35, 'rgba(255,255,255,0.85)')
    gradient.addColorStop(0.7, 'rgba(255,255,255,0.28)')
    gradient.addColorStop(1, 'rgba(255,255,255,0)')

    ctx.clearRect(0, 0, size, size)
    ctx.fillStyle = gradient
    ctx.fillRect(0, 0, size, size)

    cachedGlowTexture = new THREE.CanvasTexture(canvas)
    cachedGlowTexture.colorSpace = THREE.SRGBColorSpace
    return cachedGlowTexture
}

export function preparePowerUpAsset(
    sourceScene: THREE.Object3D,
    type: PowerUpType,
    atlasMaterial: THREE.MeshBasicMaterial,
    textures: PowerUpTextures,
): THREE.Object3D {
    const clonedScene = sourceScene.clone(true)
    const selectedIconName = powerUpIconNodeNameByType[type]
    const tintColor = powerUpColorByType[type]

    const nodesToRemove: THREE.Object3D[] = []

    clonedScene.traverse((child) => {
        const name = child.name.trim()

        if (isHitboxObjectName(name)) {
            nodesToRemove.push(child)
            return
        }

        if (allIconNodeNames.has(name) && name !== selectedIconName) {
            nodesToRemove.push(child)
            return
        }

        if (!(child instanceof THREE.Mesh)) return
        child.castShadow = false
        child.receiveShadow = false

        const isIconMesh = allIconNodeNames.has(name)

        if (Array.isArray(child.material)) {
            child.material = child.material.map((mat) =>
                createCustomMaterial(
                    mat,
                    child.geometry,
                    tintColor,
                    textures,
                    isIconMesh,
                    type,
                    atlasMaterial,
                ),
            )
        } else {
            child.material = createCustomMaterial(
                child.material,
                child.geometry,
                tintColor,
                textures,
                isIconMesh,
                type,
                atlasMaterial,
            )
        }
    })

    for (const node of nodesToRemove) {
        node.removeFromParent()
    }

    return clonedScene
}

export function createGlowBillboard(color: string): THREE.Mesh {
    const geometry = new THREE.PlaneGeometry(1, 1)
    markForDisposal(geometry)
    const material = new THREE.MeshBasicMaterial({
        color,
        map: getGlowTexture(),
        transparent: true,
        opacity: GLOW_BILLBOARD_SETTINGS.opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: true,
        depthTest: true,
        side: THREE.DoubleSide,
        toneMapped: false,
    })
    markForDisposal(material)
    const plane = new THREE.Mesh(
        geometry,
        material,
    )
    plane.name = 'GlowBillboard'
    plane.renderOrder = 1
    plane.frustumCulled = false
    plane.scale.setScalar(GLOW_BILLBOARD_SETTINGS.size)
    return plane
}

function createCustomMaterial(
    sourceMaterial: THREE.Material,
    geometry: THREE.BufferGeometry,
    tintColor: string,
    textures: PowerUpTextures,
    isIconMesh: boolean,
    type: PowerUpType,
    atlasMaterial: THREE.MeshBasicMaterial,
): THREE.Material {

    if (sourceMaterial.name === 'PowerUpOrb') {
        return createPowerUpOrbMatcapMaterial(
            textures.orbMatcap,
            tintColor,
            isIconMesh,
        )
    }

    if (sourceMaterial.name === 'PowerUpBaseGlow') {
        return createYGradientGlowMaterial(geometry, tintColor)
    }

    if (sourceMaterial.name === 'M_ColorAtlas') {
        return atlasMaterial
    }

    if (sourceMaterial.name === 'PowerUpBase') {
        const material = new THREE.MeshToonMaterial({
            color: getMaterialColor(sourceMaterial),
        })
        markForDisposal(material)
        configureImportedMaterial(material)
        return material
    }

    if (sourceMaterial.name === 'PowerUpOrb.002') {
        const material = new THREE.MeshBasicMaterial({
            map: textures.iconAtlas,
            transparent: true,
            alphaTest: 0.1,
            depthWrite: true,
            toneMapped: false,
            side: THREE.DoubleSide,
        })
        markForDisposal(material)
        return material
    }

    const cloned = sourceMaterial.clone()
    markForDisposal(cloned)
    configureImportedMaterial(cloned)
    return cloned
}

function createPowerUpOrbMatcapMaterial(
    matcap: THREE.Texture,
    tintColor: string,
    flipMatcapY: boolean,
): THREE.Material {
    const material = new THREE.MeshMatcapMaterial({
        color: '#ffffff',
        matcap,
        toneMapped: false,
        side: THREE.FrontSide,
    })
    markForDisposal(material)

    const hueColor = new THREE.Color(POWERUP_ORB_MATCAP_TINT)
    hueColor.multiply(new THREE.Color(tintColor))

    material.onBeforeCompile = (shader) => {
        shader.uniforms.uOrbHueColor = { value: hueColor }

        shader.fragmentShader = shader.fragmentShader
            .replace(
                '#include <common>',
                `#include <common>
uniform vec3 uOrbHueColor;

vec3 rgbToHsv(vec3 color) {
  vec4 k = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(color.bg, k.wz), vec4(color.gb, k.xy), step(color.b, color.g));
  vec4 q = mix(vec4(p.xyw, color.r), vec4(color.r, p.yzx), step(p.x, color.r));
  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}

vec3 hsvToRgb(vec3 color) {
  vec3 rgb = clamp(abs(mod(color.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
  rgb = rgb * rgb * (3.0 - 2.0 * rgb);
  return color.z * mix(vec3(1.0), rgb, color.y);
}`,
            )
            .replace(
                'vec2 uv = vec2( dot( x, normal ), dot( y, normal ) ) * 0.495 + 0.5;',
                `vec2 uv = vec2( dot( x, normal ), dot( y, normal ) );
uv.y *= ${flipMatcapY ? '1.0' : '-1.0'};
uv = uv * 0.495 + 0.5;`,
            )
            .replace(
                'vec4 matcapColor = texture2D( matcap, uv );',
                `vec4 matcapColor = texture2D( matcap, uv );
vec3 matcapHsv = rgbToHsv(clamp(matcapColor.rgb, 0.0, 1.0));
vec3 targetHsv = rgbToHsv(clamp(uOrbHueColor, 0.0, 1.0));
matcapHsv.x = targetHsv.x;
matcapColor.rgb = hsvToRgb(matcapHsv);`,
            )
    }

    material.customProgramCacheKey = () => `powerup-orb-matcap-hue-v2-${flipMatcapY ? 'icon' : 'orb'}`
    return material
}

function createYGradientGlowMaterial(
    geometry: THREE.BufferGeometry,
    tintColor: string,
): THREE.Material {
    const material = new THREE.MeshBasicMaterial({
        color: tintColor,
        transparent: true,
        depthWrite: true,
        toneMapped: false,
    })
    markForDisposal(material)

    geometry.computeBoundingBox()
    const minY = geometry.boundingBox?.min.y ?? -0.5
    const maxY = geometry.boundingBox?.max.y ?? 0.5
    const yRange = Math.max(maxY - minY, 0.0001)

    material.onBeforeCompile = (shader) => {
        shader.uniforms.uGradientMinY = { value: minY }
        shader.uniforms.uGradientRangeY = { value: yRange }
        shader.uniforms.uGlowOpacity = { value: POWERUP_GLOW_SETTINGS.opacity }
        shader.uniforms.uGlowPower = { value: POWERUP_GLOW_SETTINGS.power }
        shader.uniforms.uGlowBrightness = { value: POWERUP_GLOW_SETTINGS.brightness }

        shader.vertexShader = shader.vertexShader
            .replace('#include <common>',
                `#include <common>
varying vec3 vLocalPosition;`)
            .replace('#include <begin_vertex>',
                `#include <begin_vertex>
vLocalPosition = position;`)

        shader.fragmentShader = shader.fragmentShader
            .replace('#include <common>',
                `#include <common>
varying vec3 vLocalPosition;
uniform float uGradientMinY;
uniform float uGradientRangeY;
uniform float uGlowOpacity;
uniform float uGlowPower;
uniform float uGlowBrightness;`)
            .replace('vec4 diffuseColor = vec4( diffuse, opacity );',
                `vec4 diffuseColor = vec4( diffuse, opacity );
float localGradient = 1.0 - clamp((vLocalPosition.y - uGradientMinY) / uGradientRangeY, 0.0, 1.0);
localGradient = pow(localGradient, max(uGlowPower, 0.001));
diffuseColor.rgb *= uGlowBrightness;
diffuseColor.a *= localGradient * uGlowOpacity;`)
    }

    material.customProgramCacheKey = () => 'powerup-base-glow-y-gradient-v2'
    return material
}

function configureImportedMaterial(material: THREE.Material) {
    if ('side' in material && typeof material.side === 'number') {
        material.side = THREE.FrontSide
    }
    if (
        'transparent' in material &&
        typeof material.transparent === 'boolean' &&
        'depthWrite' in material &&
        typeof material.depthWrite === 'boolean'
    ) {
        material.depthWrite = !material.transparent
    }
    if ('depthTest' in material && typeof material.depthTest === 'boolean') {
        material.depthTest = true
    }
}

function hasColorProperty(
    material: THREE.Material,
): material is THREE.Material & { color: THREE.Color } {
    return 'color' in material && material.color instanceof THREE.Color
}

function getMaterialColor(material: THREE.Material): string {
    return hasColorProperty(material)
        ? `#${material.color.getHexString()}`
        : '#ffffff'
}

function isHitboxObjectName(name: string) {
    const lower = name.toLowerCase()
    return (
        lower.includes('hitbox') ||
        lower.includes('collider') ||
        lower.includes('collision')
    )
}
